import { Billboard } from '@react-three/drei'
import { suspend } from 'suspend-react'
import * as THREE from 'three'

import AsyncBoundary from '@components/AsyncBoundary.tsx'
import FallbackCategoryIcon from '@components/storeMap/assets/fallbackCatIcon.svg?no-inline'
import PinIcon from '@components/storeMap/assets/pinCat.svg?no-inline'
import { type ShelfGroup } from '@components/storeMap/categoryGrouping/shelfGrouping'
import { iconMap } from '@components/storeMap/categoryGrouping/util'
import { useRegisterForAnimation } from '@components/storeMap/floor/3d/AnimationContext'
import { TextWithLabel } from '@components/storeMap/floor/3d/components/Text'
import { loadTexture, useBobbing } from '@components/storeMap/floor/3d/util'
import { useShelfCategoryGroups } from '@components/storeMap/utils'
import useTypedTranslation from '@hooks/useTypedTranslation.ts'

import type { ShelfCategory } from '@components/storeMap/categoryGrouping/util'

const PIN_WIDTH = 0.8
const PIN_HEIGHT = PIN_WIDTH * (38 / 34) // Maintain SVG aspect ratio (34x38)

function CategoryMarkers() {
  const { data: shelfCategories } = useShelfCategoryGroups()
  useRegisterForAnimation()

  if (!shelfCategories) return null

  return shelfCategories.map(group => (
    <WrappedCategoryMarker
      key={group.id}
      category={group.category}
      position={group.center}
    />
  ))
}

function WrappedCategoryMarker(props: CategoryMarkerProps) {
  return (
    <AsyncBoundary>
      <CategoryMarker {...props} />
    </AsyncBoundary>
  )
}

interface CategoryMarkerProps {
  category: ShelfCategory
  position: ShelfGroup['center']
}

function CategoryMarker({ category, position }: CategoryMarkerProps) {
  const { t } = useTypedTranslation()
  const bobbingRef = useBobbing()

  const myPosition = [position.x, 2.3, position.y] as const

  const pinTexture = suspend(() => loadTexture({ imageUrl: PinIcon, size: 100 }), [PinIcon])

  const isCategoryKnown = !!iconMap[category]
  const iconUrl = isCategoryKnown ? iconMap[category] : FallbackCategoryIcon
  const iconTexture = suspend(() => loadTexture({ imageUrl: iconUrl, size: 100 }), [iconUrl])

  return (
    <group ref={bobbingRef} position={myPosition}>
      <Billboard follow={true} lockX={false} lockY={false} lockZ={false}>
        <mesh position={[0, PIN_HEIGHT, 0]}>
          <planeGeometry args={[PIN_WIDTH, PIN_HEIGHT]} />
          <meshBasicMaterial map={pinTexture} transparent side={THREE.DoubleSide} />
        </mesh>

        <mesh position={[0, PIN_HEIGHT + 0.05, 0.01]}>
          <planeGeometry args={[PIN_WIDTH * 0.7, PIN_WIDTH * 0.7]} />
          <meshBasicMaterial map={iconTexture} transparent side={THREE.DoubleSide} />
        </mesh>

        <TextWithLabel position={[0, PIN_HEIGHT - 0.7, 0]}>
          {isCategoryKnown ?
            t(`shelfCategories.${category}`) :
            category
          }
        </TextWithLabel>
      </Billboard>

      {/* shadow thrower */}
      <mesh position={[0, PIN_HEIGHT, 0]} castShadow>
        <sphereGeometry args={[0.4, 32, 32]} />
        <meshBasicMaterial colorWrite={false} depthWrite={false} />
      </mesh>
    </group>
  )
}

export default CategoryMarkers